import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight, Newspaper, ChevronRight, Send, Bell, Coffee } from 'lucide-react'; // Added Coffee for the empty state
import { Link } from 'react-router-dom';
import axios from 'axios';

// ... (Interface remains the same)

interface NewsPost {
    id: number;
    title: string;
    slug: string;
    excerpt: string;
    category: string;
    published_at: string;
    image: string;
}

const NewsFeed = () => {
    const [news, setNews] = useState<NewsPost[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchNews = async () => {
            try {
                const response = await axios.get('https://api.ibadansouthanglicandiocese.org.ng/api/v1/news');
                // Taking only the latest 3
                setNews(response.data.news.slice(0, 3));
            } catch (err) {
                console.error('Error fetching news:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchNews();
    }, []);

    if (loading) return null;

    return (
        <section className="py-4 bg-white overflow-hidden">
            <div className="max-w-7xl mx-auto px-6">

                {/* Section Header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
                    <div className="max-w-2xl">
                        <div className="flex items-center gap-2 text-amber-600 mb-4 uppercase tracking-[0.4em] text-[10px] font-bold">
                            <Newspaper size={16} />
                            <span>The Diocesan Journal</span>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-serif font-bold text-slate-900 leading-tight">
                            Latest <span className="text-amber-500 italic">Insights</span> & News
                        </h2>
                    </div>
                    {/* Only show 'View All' link if there are posts */}
                    {news.length > 0 && (
                        <Link
                            to="/news"
                            className="group flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-slate-400 hover:text-amber-600 transition-colors"
                        >
                            View All Stories
                            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                    )}
                </div>

                {/* News Grid / Empty State Logic */}
                {news.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-4">
                        {news.map((item, index) => (
                            <motion.article
                                key={item.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="group"
                            >
                                <Link to={`/news/${item.slug}`} className="block space-y-6">
                                    <div className="relative aspect-[4/3] overflow-hidden rounded-[2rem] bg-slate-100 shadow-lg shadow-slate-200/50">
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                        />
                                        <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-md px-4 py-1.5 rounded-xl text-[9px] font-bold uppercase tracking-widest text-slate-900">
                                            {item.category}
                                        </div>
                                    </div>

                                    <div className="space-y-3 px-2">
                                        <div className="flex items-center gap-2 text-slate-400 text-[10px] font-bold uppercase tracking-widest">
                                            <Calendar size={12} className="text-amber-500" />
                                            {new Date(item.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </div>
                                        <h3 className="text-xl font-serif font-bold text-slate-900 group-hover:text-amber-600 transition-colors leading-snug line-clamp-2">
                                            {item.title}
                                        </h3>
                                        <p className="text-slate-500 text-sm leading-relaxed font-serif italic line-clamp-2">
                                            {item.excerpt}
                                        </p>
                                        <div className="pt-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-900 group-hover:gap-4 transition-all">
                                            Read Article <ChevronRight size={14} className="text-amber-500" />
                                        </div>
                                    </div>
                                </Link>
                            </motion.article>
                        ))}
                    </div>
                ) : (
                    /* --- NO NEWS PLACEHOLDER --- */
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex flex-col items-center justify-center py-20 px-6 mb-20 bg-slate-50 rounded-[3rem] border border-dashed border-slate-200 text-center"
                    >
                        <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm mb-6 text-amber-500">
                            <Coffee size={32} />
                        </div>
                        <h3 className="text-2xl font-serif font-bold text-slate-900 mb-2">A Brief Moment of Silence</h3>
                        <p className="text-slate-500 font-serif italic max-w-sm">
                            We are currently preparing new updates. Check back soon or subscribe below to be the first to know.
                        </p>
                    </motion.div>
                )}
            </div>
        </section>
    );
};

export default NewsFeed;